import { DatabaseOutlined, FolderOutlined, GoldOutlined } from '@ant-design/icons';
import { getIdMap, isNullOrUndefined } from '../helpers/helpers';
import { FULL_EXPORT_ORDER, keyToColumnnMap } from './materials.constants';

export default {};

export const MATERIAL_COST_MAX_DECIMALS = 5;
export const MATERIAL_MARKUP_MAX_DECIMALS = 5;

export const hasMarkup = (markup) => markup || markup === 0;

const roundMarkupPercent = (value) => (
  Number.parseFloat(Number(value).toFixed(MATERIAL_MARKUP_MAX_DECIMALS))
);

export const getMarkupPercent = (markup) => (
  hasMarkup(markup) ? roundMarkupPercent(markup * 100) : markup
);
export const getMarkupString = (markup) => (hasMarkup(markup) ? `${getMarkupPercent(markup)} %` : '');

export const getPriceFloat = (cost, markup, discount = 0) => (
  parseFloat((cost * (1 + (markup / 100)) * (1 - discount)).toFixed(2))
);
export const getPrice = (cost, markup) => `$ ${getPriceFloat(cost, markup).toFixed(2)}`;

export const getMarkupPercentFromPrice = (price, cost) => {
  const costNumber = Number.parseFloat(cost);
  const priceNumber = Number.parseFloat(price);
  if (!costNumber || Number.isNaN(costNumber) || Number.isNaN(priceNumber)) {
    return 0;
  }
  return roundMarkupPercent(((priceNumber / costNumber) - 1) * 100);
};

export const getDerivedPriceValue = (cost, markupDecimal, isDisplay = false) => {
  if (isNullOrUndefined(cost) || !hasMarkup(markupDecimal || 0)) {
    return isDisplay ? '$ 0.00' : '0.00';
  }
  const markupPercent = getMarkupPercent(markupDecimal || 0);
  return isDisplay ? getPrice(cost, markupPercent) : getPriceFloat(cost, markupPercent);
};

export const getUpdatedPricingFields = ({
  changedValues,
  allValues,
  priceKey = 'price',
  markupKey = 'markup',
  costKey = 'cost',
}) => {
  const cost = allValues[costKey];
  const markup = allValues[markupKey] ?? 0;
  // if the price has changed, update the markup
  if (priceKey in changedValues) {
    return {
      [markupKey]: getMarkupPercentFromPrice(allValues[priceKey], cost),
    };
  }
  // if the cost or markup has changed, update the price
  if (
    (costKey in changedValues || markupKey in changedValues)
    && !isNullOrUndefined(cost)
  ) {
    const costNumber = Number.parseFloat(cost);
    const markupNumber = Number.parseFloat(markup);
    if (Number.isFinite(costNumber) && Number.isFinite(markupNumber)) {
      const roundedMarkup = roundMarkupPercent(markupNumber);
      return {
        [priceKey]: getPriceFloat(costNumber, roundedMarkup),
        ...(costKey in changedValues ? { [markupKey]: roundedMarkup } : {}),
      };
    }
  }
  return {};
};

export const changeIsNumber = (change) => (
  change !== null
  && change !== undefined
  && Number.isFinite(change)
);

export const prepareMaterialLocations = ({
  oldLocations = [],
  newLocations = [],
}) => {
  const oldMap = getIdMap(oldLocations);
  const newMap = getIdMap(newLocations);

  const deletedLocations = [];
  const updatedLocations = [];
  const createdLocations = [];

  oldLocations.forEach((oldLoc) => {
    const {
      id,
      quantity: oldQuant,
      quantityAllocated: oldQuantAlloc,
      equipmentId: oldEqId,
      globalMaterialLocationsId: oldGloLocId,
    } = oldLoc;
    if (!(id in newMap)) {
      deletedLocations.push(id);
    } else {
      const {
        [id]: {
          quantity: newQuant,
          quantityAllocated: newQuantAlloc,
          equipmentId: newEqId,
          globalMaterialLocationsId: newGloLocId,
        } = {},
      } = newMap;

      if (
        oldQuant !== newQuant
        || oldEqId !== newEqId
        || oldGloLocId !== newGloLocId
        || oldQuantAlloc !== newQuantAlloc
      ) {
        updatedLocations.push({
          id,
          quantity: newQuant,
          quantityAllocated: newQuantAlloc,
          equipmentId: newEqId,
          globalMaterialLocationsId: newGloLocId,
        });
      }
    }
  });

  newLocations.forEach((newLoc) => {
    const {
      id, quantity, quantityAllocated, equipmentId, isNew, globalMaterialLocationsId,
    } = newLoc;
    if (
      (!(id in oldMap) || isNew)
      && (equipmentId || globalMaterialLocationsId)
      && (quantity || quantity === 0)
      && (quantityAllocated || quantityAllocated === 0)
    ) {
      createdLocations.push({
        quantity,
        quantityAllocated,
        equipmentId,
        globalMaterialLocationsId,
      });
    }
  }, []);
  return {
    deletedLocations,
    updatedLocations,
    createdLocations,
  };
};

export const getMaterialIcon = (record) => {
  if (record.isFolder) return <FolderOutlined />;
  if (record.isAssembly) return <DatabaseOutlined />;
  return <GoldOutlined />;
};

export const getRelevantDeleteWarning = (
  materials,
  materialIds,
) => {
  const materialIdSet = new Set(materialIds);
  const relevantMaterials = new Set();
  const relevantAssemblyMaterials = Object.values(materials).filter((material) => (
    material.assemblyMaterials?.some((assemblyMaterial) => {
      if (materialIdSet.has(assemblyMaterial.materialId)) {
        relevantMaterials.add(assemblyMaterial.materialId);
        return true;
      }

      return false;
    })
  ));

  return relevantAssemblyMaterials.length > 0
    ? (
      <>
        <div>
          The following materials are used in {relevantAssemblyMaterials.length} assemblies:
        </div>
        <ul>
          {Array.from(relevantMaterials).map((materialId) => (
            <li key={materialId}>{materials[materialId].name}</li>
          ))}
        </ul>
      </>
    )
    : null;
};

export const inCurrentFolder = (material, materialsMap, currentFolderId) => {
  const materialGroupId = material.groupId;

  if (!currentFolderId) return true;

  if (materialGroupId === currentFolderId) return true;

  let current = materialsMap[materialGroupId];
  while (current) {
    if (current.id === currentFolderId) return true;
    current = materialsMap[current.groupId];
  }

  return false;
};

export const getMaterialPath = (material, materials) => {
  let current = material;
  const pathArray = [];
  const crumbs = [];

  while (current.groupId) {
    const parent = materials[current.groupId];
    if (!parent) break;
    pathArray.unshift(parent.name);
    crumbs.unshift({ title: parent.name, groupId: parent.id });
    current = parent;
  }

  const fullPath = pathArray.length > 0 ? pathArray.join(' / ') : null;

  return { fullPath, crumbs };
};

export const formatExportedMaterial = ({
  data,
  selectedColumns,
  costcodeMap,
  equipmentMap,
  globalMaterialLocationsMap,
  vendorMap,
}) => {
  const exportColumns = FULL_EXPORT_ORDER.filter((key) => ['name', 'groupId', 'active'].includes(key) || selectedColumns?.has?.(key));

  return data
    .map((item) => {
      const filteredItem = exportColumns.reduce((acc, key) => {
        if (key === 'groupId') {
          acc.Folder = item?.fullPath ?? '';
        } else if (key === 'active') {
          const status = item[key];
          acc.Status = status ? 'Active' : 'Archived';
        } else if (key === 'cost') {
          acc['Material Cost ($)'] = item?.cost || '';
        } else if (key === 'labourCost') {
          acc['Labour Cost ($)'] = item?.labourCost || '';
        } else if (key === 'price') {
          const { cost } = item;
          const { markup } = item;
          acc['Material Price ($)'] = hasMarkup(markup) && cost ? (cost * (1 + markup)).toFixed(2) : '';
        } else if (key === 'markup') {
          acc['Material Markup (%)'] = hasMarkup(item?.markup) && item?.cost ? item.markup * 100 : '';
        } else if (key === 'labourMarkup') {
          acc['Labour Markup (%)'] = hasMarkup(item?.labourMarkup) && item?.labourCost ? item.labourMarkup * 100 : '';
        } else if (key === 'labourPrice') {
          const { labourCost } = item;
          const { labourMarkup } = item;
          acc['Labour Price ($)'] = hasMarkup(labourMarkup) && labourCost ? (labourCost * (1 + labourMarkup)).toFixed(2) : '';
        } else if (key === 'quantityAvailable') {
          const quantity = item.quantity ?? 0;
          const quantityAllocated = item.quantityAllocated ?? 0;
          acc['Quantity Available'] = quantity - quantityAllocated;
        } else if (key === 'costcode') {
          const costcode = costcodeMap?.[item.costcodeId];
          acc['Cost Code'] = costcode?.name ?? '';
        } else if (key === 'vendor') {
          const vendor = vendorMap?.[item.vendorId];
          acc.Vendor = vendor?.name ?? '';
        } else if (key === 'locations') {
          const { locations } = item;
          const locationNames = locations.map((location) => {
            const { globalMaterialLocationsId, equipmentId } = location;

            const {
              [equipmentId]: {
                name,
              } = {},
            } = equipmentMap;

            const {
              [globalMaterialLocationsId]: {
                locationText: globalMaterialLocationsText,
              } = {},
            } = globalMaterialLocationsMap;

            return name || globalMaterialLocationsText;
          }).join(', ');

          acc.Locations = locationNames;
        } else {
          acc[keyToColumnnMap[key]] = item[key] ?? null;
        }
        return acc;
      }, {});
      return filteredItem;
    });
};
